import { useContext, useState } from "react"
import { Check, ChevronDown } from "lucide-react"
import { XmppContext } from "@/stream/xmppClient"
import { Button } from "@/components/ui/button"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { cn } from "@/lib/utils"
import {
  carMoveWrites,
  carShortName,
  driverDisplayName,
  isFreeVehicle,
  vehicleName,
  type CrewRow,
  type RouteRow,
} from "@/lib/crewBoard"
import { normalizePlate } from "@/lib/plates"
import { useAppSelector } from "@/reducers/hooks"
import { selectDriverNameOrder } from "@/reducers/serviceConfigSlice"
import { driverName } from "@/lib/driverNameOrder"
import { saveCrewChat } from "./saveCrewChat"
import { usePendingPick } from "./usePendingPick"
import { useTranslation } from "react-i18next"

type CrewCarCellProps = {
  routeRow: RouteRow
  rows: CrewRow[]
}

const seatedName = (row: CrewRow): string =>
  row.driver ? driverDisplayName(row.driver) : (row.chat?.driverA ?? "")

const VehicleLabel = ({ row }: { row: CrewRow }) => {
  const name = vehicleName(row.car)
  return (
    <>
      {name}
      {name !== row.car.plates && (
        <span className="text-muted-foreground"> {row.car.plates}</span>
      )}
    </>
  )
}

/**
 * The vehicle on a route. The chat stays with its vehicle and the route lives
 * in the chat's name, so picking a vehicle renames two chats and moves the
 * route's driver across (see carMoveWrites).
 */
export const CrewCarCell = ({ routeRow, rows }: CrewCarCellProps) => {
  const { t } = useTranslation()
  const { client } = useContext(XmppContext)
  const nameOrder = useAppSelector(selectDriverNameOrder)
  const [open, setOpen] = useState(false)
  // undefined: no question open; null: asking about taking the vehicle off.
  const [asking, setAsking] = useState<CrewRow | null | undefined>(undefined)

  const { route, row: from } = routeRow
  const currentId = from?.car.id ?? ""
  const { pending, saving, save } = usePendingPick(currentId)
  const shownId = pending ?? currentId
  const shown = rows.find((row) => row.car.id === shownId)

  const others = rows.filter((row) => row.car.id !== currentId)
  const free = others.filter(isFreeVehicle)
  const taken = others.filter((row) => !isFreeVehicle(row))

  const consequences = (target: CrewRow | null): string[] => {
    if (!target) {
      if (!from) return []
      return [
        t("fleet.crewBoard.vehicleOffRoute", {
          vehicle: carShortName(from),
          route,
        }),
        ...(from.chat?.driverA
          ? [
              t("fleet.crewBoard.driverLeaves", {
                driver: seatedName(from),
                vehicle: carShortName(from),
              }),
            ]
          : []),
      ]
    }
    const takenFromRoute = !!target.route && target.route !== route
    return [
      ...(from
        ? [
            from.driver
              ? t("fleet.crewBoard.routeAndDriverMove", {
                  driver: driverDisplayName(from.driver),
                  route,
                  from: carShortName(from),
                  to: carShortName(target),
                })
              : t("fleet.crewBoard.routeMoves", {
                  route,
                  from: carShortName(from),
                  to: carShortName(target),
                }),
            t("fleet.crewBoard.stopsStay", { vehicle: carShortName(from) }),
          ]
        : []),
      ...(takenFromRoute
        ? [
            t("fleet.crewBoard.takenFromRoute", {
              vehicle: carShortName(target),
              route: target.route,
            }),
          ]
        : []),
      ...(target.chat?.driverA && from?.driver
        ? [
            t("fleet.crewBoard.driverLeaves", {
              driver: seatedName(target),
              vehicle: carShortName(target),
            }),
          ]
        : []),
      ...(target.chat?.driverA && !from?.driver && takenFromRoute
        ? [
            t("fleet.crewBoard.driverStays", {
              driver: seatedName(target),
              vehicle: carShortName(target),
            }),
          ]
        : []),
    ]
  }

  const write = (target: CrewRow | null) =>
    save(target?.car.id ?? "", async () => {
      for (const change of carMoveWrites(route, from, target ?? undefined)) {
        await saveCrewChat(client, change)
      }
    })

  const handleSelect = (target: CrewRow | null) => {
    setOpen(false)
    if ((target?.car.id ?? "") === shownId) return
    if (consequences(target).length) {
      setAsking(target)
      return
    }
    void write(target)
  }

  const option = (row: CrewRow) => (
    <CommandItem
      key={row.car.id}
      value={`${vehicleName(row.car)} ${row.car.plates} ${normalizePlate(row.car.plates)}`}
      // Double crew chats are support-managed: upsert_muc refuses them.
      disabled={row.doubleCrew}
      onSelect={() => handleSelect(row)}
    >
      <Check className="mr-2 h-4 w-4 opacity-0" />
      <span className="truncate">
        <VehicleLabel row={row} />
      </span>
      {!isFreeVehicle(row) && (
        <span className="ml-auto whitespace-nowrap pl-2 text-xs text-muted-foreground">
          {row.doubleCrew
            ? t("fleet.crewBoard.doubleCrew")
            : [row.route, row.driver && driverName(row.driver, nameOrder)]
                .filter(Boolean)
                .join(" · ")}
        </span>
      )}
    </CommandItem>
  )

  return (
    <>
      {/* Not modal: the confirmation opens from the list (see CrewDriverCell). */}
      <Popover open={open} onOpenChange={setOpen} modal={false}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-label={t("fleet.crewBoard.vehicleFor", { route })}
            aria-expanded={open}
            disabled={saving || from?.doubleCrew}
            className="w-full justify-between font-normal"
          >
            <span className={cn("truncate", !shown && "text-muted-foreground")}>
              {shown ? (
                <VehicleLabel row={shown} />
              ) : (
                t("fleet.crewBoard.selectVehicle")
              )}
            </span>
            <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="z-[9999] w-[var(--radix-popover-trigger-width)] min-w-[340px] p-0"
          sideOffset={5}
        >
          <Command>
            <CommandInput placeholder={t("fleet.crewBoard.searchVehicle")} />
            <CommandList className="max-h-[300px] overflow-y-auto">
              <CommandEmpty>{t("fleet.crewBoard.noVehicleFound")}</CommandEmpty>
              {from && (
                <CommandGroup>
                  <CommandItem
                    value={t("fleet.crewBoard.none")}
                    onSelect={() => handleSelect(null)}
                  >
                    <Check className="mr-2 h-4 w-4 opacity-0" />
                    <span className="text-muted-foreground">
                      {t("fleet.crewBoard.none")}
                    </span>
                  </CommandItem>
                </CommandGroup>
              )}
              {free.length > 0 && (
                <CommandGroup heading={t("fleet.crewBoard.freeGroup")}>
                  {free.map(option)}
                </CommandGroup>
              )}
              {taken.length > 0 && (
                <CommandGroup heading={t("fleet.crewBoard.takenGroup")}>
                  {taken.map(option)}
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      <AlertDialog
        open={asking !== undefined}
        onOpenChange={(isOpen) => {
          if (!isOpen) setAsking(undefined)
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t(
                asking === null
                  ? "fleet.crewBoard.removeVehicleTitle"
                  : "fleet.crewBoard.changeVehicleTitle",
                { route },
              )}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {asking !== undefined && consequences(asking).join(" ")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (asking !== undefined) void write(asking)
                setAsking(undefined)
              }}
            >
              {asking === null
                ? t("common.remove")
                : t("fleet.crewBoard.changeVehicle")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
